import { Link, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { Upload, FolderOpen } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useServerConfig } from "@/hooks/useServerConfig";
import { cn } from "@/lib/utils";

export function Layout() {
  const { t } = useTranslation();
  const location = useLocation();
  const { config } = useServerConfig();

  const logoSrc = config?.customLogo ?? "/logo.svg";
  const title = config?.customTitle ?? t("common.appName");

  useEffect(() => {
    document.title = `${title} | ${t("common.tabSubtitle")}`;
  }, [title, t]);

  const navItems = [
    { to: "/", label: t("nav.upload"), icon: Upload },
    { to: "/uploads", label: t("nav.myUploads"), icon: FolderOpen },
  ];

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-bold tracking-tight"
          >
            <img src={logoSrc} alt="" className="h-6 w-6" />
            <span>{title}</span>
          </Link>

          <nav className="flex items-center gap-1">
            {navItems.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
                  (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to))
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </Link>
            ))}
            <LanguageSwitcher />
            <ThemeToggle />
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}

